import type {
  LogisticsIncoterm,
  OrderBlockMessage,
  OrderItem,
  Quantity,
  UnitCode,
} from '~/graphql/graphql';
import {
  convertPiecesToUnit,
  toStandardUnits,
  unitsPerPiece,
} from './unitConversion';
import type { VehicleOption } from './vehicleGroups';
import { VEHICLE_TYPE_ORDER } from './vehicleGroups';
import { incoTermToDeliveryMethod } from '~/hooks/useTransportState';
import type { DomesticDeliveryMethod } from '~/types/general';

export type { VehicleOption };
export { VEHICLE_TYPE_ORDER };

export function getUnprocessedBlockMessages(
  blockMessages: readonly OrderBlockMessage[]
) {
  return blockMessages.filter((message) => !message.processedAt);
}

export function getFirstAvailableVehicle(
  facilityVehicles: VehicleOption[] | null | undefined,
  supportedVehicleIds: string[] | undefined
): VehicleOption | undefined {
  if (!facilityVehicles || facilityVehicles.length === 0) return undefined;
  if (supportedVehicleIds === undefined) return undefined;

  const filteredVehicles = facilityVehicles.filter((vehicle) =>
    supportedVehicleIds.includes(vehicle.id)
  );

  return [...filteredVehicles].sort((vehicleA, vehicleB) => {
    const typeOrder =
      VEHICLE_TYPE_ORDER.indexOf(vehicleA.vehicleType) -
      VEHICLE_TYPE_ORDER.indexOf(vehicleB.vehicleType);
    if (typeOrder !== 0) return typeOrder;
    return vehicleA.name.localeCompare(vehicleB.name);
  })[0];
}
// Small epsilon used in bundle-quantity rounding to absorb floating-point drift.
// Without it, a value that is mathematically exactly N bundles can be computed as
// N+ε due to IEEE-754 error, causing Math.ceil to round up to N+1 bundles (or
// Math.floor to round down to N-1). The value is chosen to be large enough to
// cover typical float drift (~1e-10) but small enough never to cross a real
// bundle boundary (minimum bundle ≈ 0.001 MBF).
export const ROUNDING_EPSILON = 1e-5;

// Epsilon used to detect whether totalLoads is already at (or negligibly close to)
// a whole-number boundary. Prevents floating-point drift from triggering spurious
// top-up / fill suggestions.
const LOAD_BOUNDARY_EPSILON = 0.0001;

/**
 * Returns the fraction of a load still needed to reach the next whole load.
 * e.g. 4.56 loads → 0.44, 5.00 loads → 0 (already whole), 0 loads → 0.
 */
export function partialLoadFraction(totalLoads: number): number {
  const remainder = totalLoads % 1;
  return totalLoads > 0 &&
    remainder > LOAD_BOUNDARY_EPSILON &&
    remainder < 1 - LOAD_BOUNDARY_EPSILON
    ? 1 - remainder
    : 0;
}

export function getAddToCartQuantity(
  neededQuantity: number,
  availableStock: number,
  minimumOrder: number
): number {
  if (availableStock === 0) {
    return Math.max(neededQuantity, minimumOrder);
  }

  return Math.min(
    Math.max(minimumOrder, Math.max(0, neededQuantity)),
    availableStock
  );
}

export function roundUpToBundleQty(
  rawQty: number,
  piecesPerBundle: number,
  unitCode: UnitCode,
  lengthInches: number,
  widthInches: number,
  thicknessInches: number
): number {
  if (
    !piecesPerBundle ||
    rawQty <= 0 ||
    !lengthInches ||
    !widthInches ||
    !thicknessInches
  ) {
    return rawQty;
  }
  const perPiece = unitsPerPiece(
    unitCode,
    lengthInches,
    widthInches,
    thicknessInches
  );
  if (!perPiece) return rawQty;
  const pieces = rawQty / perPiece;
  const wholeBundles = Math.ceil(pieces / piecesPerBundle - ROUNDING_EPSILON);
  return convertPiecesToUnit(
    wholeBundles * piecesPerBundle,
    unitCode,
    lengthInches,
    widthInches,
    thicknessInches
  );
}

type ProductDimensions = {
  nominalWidthInches?: number | null;
  actualWidthInches?: number | null;
  nominalThicknessInches?: number | null;
  actualThicknessInches?: number | null;
  actualLengthInches?: number | null;
};

type LengthDimensions =
  | {
      nominalLengthInches?: number | null;
      actualLengthInches?: number | null;
    }
  | null
  | undefined;

export type BundledLine = {
  platformProductsLengths?: {
    isNominal?: boolean | null;
    length?: LengthDimensions;
    platformProduct?:
      | (ProductDimensions & {
          piecesPerBundle?: { value: number } | null;
        })
      | null;
  } | null;
  effectivePiecesPerBundle: number;
  quantity: Pick<Quantity, 'value' | 'unit'>;
};

export type ProductDimensionsInches = {
  lengthInches: number;
  widthInches: number;
  thicknessInches: number;
};

export function getPlatformProductDimensions(
  product: ProductDimensions | null | undefined,
  length: LengthDimensions,
  isNominal: boolean | null | undefined
): ProductDimensionsInches {
  return {
    widthInches: isNominal
      ? (product?.nominalWidthInches ?? product?.actualWidthInches ?? 0)
      : (product?.actualWidthInches ?? 0),
    thicknessInches: isNominal
      ? (product?.nominalThicknessInches ?? product?.actualThicknessInches ?? 0)
      : (product?.actualThicknessInches ?? 0),
    lengthInches: isNominal
      ? (length?.nominalLengthInches ?? length?.actualLengthInches ?? 0)
      : (product?.actualLengthInches ?? length?.actualLengthInches ?? 0),
  };
}

export type VehicleCapacityPayload = {
  transportVehicle: { id: string };
  capacity: Quantity;
};

export function getVehiclePayload(
  payloads: Array<VehicleCapacityPayload | null> | null | undefined,
  vehicleId: string
): Quantity | null {
  return (
    payloads?.find((p) => p?.transportVehicle.id === vehicleId)?.capacity ??
    null
  );
}

/**
 * A cart line as the cart screens read it. `quantityStored` is the server's
 * piece count, which the optimistic override also carries, and is what
 * piece-priced totals are computed from.
 */
export type CartOrderItem = OrderItem & {
  quantityStored?: Pick<Quantity, 'value'>;
};

export type CalcItemLoadsInput = {
  quantity: { value: number; unit: UnitCode };
  platformProductsLengths: {
    isNominal?: boolean | null;
    platformProduct: {
      nominalThicknessInches?: number | null;
      nominalWidthInches?: number | null;
      actualThicknessInches?: number | null;
      actualWidthInches?: number | null;
      actualLengthInches?: number | null;
      standardUnitsCode?: UnitCode | null;
    };
    platformProductVehicleCapacities?: Array<{
      transportVehicle: { id: string };
      capacity: Quantity;
    } | null> | null;
    length?: {
      nominalLengthInches?: number | null;
      actualLengthInches?: number | null;
    } | null;
  };
};

export function calcItemLoads(
  item: CalcItemLoadsInput,
  vehiclePayload: Quantity | null | undefined
): number {
  if (!vehiclePayload || vehiclePayload.value <= 0) return 0;

  const ppl = item.platformProductsLengths;
  const p = ppl?.platformProduct;
  if (!p) return 0;

  const fromUnit = item.quantity.unit;
  const toUnit = vehiclePayload.unit;

  let qty: number;
  if (fromUnit === toUnit) {
    qty = item.quantity.value;
  } else {
    const { widthInches, thicknessInches, lengthInches } =
      getPlatformProductDimensions(p, ppl?.length, ppl?.isNominal);

    if (lengthInches <= 0 || widthInches <= 0 || thicknessInches <= 0) {
      return 0;
    }

    qty = toStandardUnits(
      item.quantity.value,
      fromUnit,
      toUnit,
      lengthInches,
      widthInches,
      thicknessInches
    );
  }

  return qty / vehiclePayload.value;
}

type SupportedVehicleIdsItem = {
  platformProductsLengths?: {
    platformProductVehicleCapacities?: Array<Pick<
      VehicleCapacityPayload,
      'transportVehicle'
    > | null> | null;
  } | null;
};

export function calcSupportedVehicleIds(
  items: SupportedVehicleIdsItem[]
): string[] | undefined {
  if (items.length === 0) return undefined;

  const vehicleIdSets = items.map((item) => {
    const payloads =
      item.platformProductsLengths?.platformProductVehicleCapacities;
    return new Set(
      payloads?.flatMap((p) => (p ? [p.transportVehicle.id] : [])) ?? []
    );
  });

  if (vehicleIdSets.some((set) => set.size === 0)) return undefined;

  const [first, ...rest] = vehicleIdSets;
  const intersection = [...first].filter((id) =>
    rest.every((set) => set.has(id))
  );
  return intersection;
}

type GradeInfoInput = {
  platformProduct: {
    properties?: { grade?: { code?: string | null } | null } | null;
    actualThicknessInches?: number | null;
    actualWidthInches?: number | null;
  };
};

export function getGradeInformation(
  platformProductLength: GradeInfoInput
): string {
  const grade = platformProductLength.platformProduct.properties?.grade?.code;
  const actualThicknessInches =
    platformProductLength.platformProduct.actualThicknessInches;
  const actualWidthInches =
    platformProductLength.platformProduct.actualWidthInches;

  const resp: string[] = [];
  if (grade) {
    resp.push(grade);
  }
  if (actualThicknessInches && actualWidthInches && grade) {
    resp.push('•');
  }

  if (actualThicknessInches && actualWidthInches) {
    resp.push(`${actualThicknessInches} x ${actualWidthInches}`);
  }

  return resp.length === 0 ? 'N/A' : resp.join(' ');
}

type OrderItemForPackingTotal = {
  effectivePrice?: { amount: number } | null;
  price?: { amount: number } | null;
  quantity: { value: number };
};

type PackingListItemForTotal = {
  price: { amount: number };
  quantity: { value: number };
};

export function computeOrderItemsTotal(
  items: OrderItemForPackingTotal[]
): number {
  return items.reduce(
    (sum, item) =>
      sum +
      (item.effectivePrice?.amount ?? item.price?.amount ?? 0) *
        item.quantity.value,
    0
  );
}

export function computePackedItemsTotal(
  items: PackingListItemForTotal[]
): number {
  return items.reduce(
    (sum, item) => sum + item.price.amount * item.quantity.value,
    0
  );
}

type PricedAmount = { amount: number; currency?: string };

type OrderItemForListedPrice = {
  price?: PricedAmount | null;
  suggestedPrice?: PricedAmount | null;
  pickupPrice?: PricedAmount | null;
  deliveryPrice?: PricedAmount | null;
};

/**
 * Read the seller's LISTED price for the order's delivery mode — pickupPrice when the
 * order's incoterm is EXW, deliveryPrice otherwise — falling back to the plain `price`
 * field when no pickup/delivery split is set. Do NOT read item.effectivePrice here — the
 * backend's OrderItem.getEffectivePrice() collapses to suggestedPrice whenever a buyer
 * suggestion exists, which would make this per-line listed price row render the same
 * figure as the amber suggested-price box below it.
 */
export function getListedPricePerUnit(
  item: OrderItemForListedPrice,
  incoTerm?: string | null
): {
  amount: number | null;
  currency: string;
} {
  const modePrice = getModeAwarePrice(item, incoTerm);
  return {
    amount: modePrice?.amount ?? item.price?.amount ?? null,
    currency:
      modePrice?.currency ??
      item.price?.currency ??
      item.suggestedPrice?.currency ??
      'USD',
  };
}

export function getCartLinePrice(
  item: OrderItemForListedPrice & { effectivePrice?: PricedAmount | null },
  incoTerm?: string | null
): number | null {
  return (
    item.suggestedPrice?.amount ??
    getListedPricePerUnit(item, incoTerm).amount ??
    item.effectivePrice?.amount ??
    null
  );
}

/** Price the market comparison uses; null on delivered carts, where listed prices include freight. */
export function getMarketComparisonPrice(
  item: OrderItemForListedPrice & { effectivePrice?: PricedAmount | null },
  incoTerm?: string | null
): number | null {
  return incoTermToDeliveryMethod(incoTerm) === 'pickup'
    ? getCartLinePrice(item, incoTerm)
    : null;
}

type OrderItemForListedPricePerPiece = {
  pricePerPiece?: PricedAmount | null;
  pickupPricePerPiece?: PricedAmount | null;
  deliveryPricePerPiece?: PricedAmount | null;
};

export function getListedPricePerPiece(
  item: OrderItemForListedPricePerPiece,
  incoTerm?: string | null
): PricedAmount | null {
  const modePrice = getModeAwarePrice(
    {
      price: item.pricePerPiece,
      pickupPrice: item.pickupPricePerPiece,
      deliveryPrice: item.deliveryPricePerPiece,
    },
    incoTerm
  );
  return modePrice ?? item.pricePerPiece ?? null;
}

export function getEffectivePricePerPiece(
  item: OrderItemForListedPricePerPiece & {
    suggestedPricePerPiece?: PricedAmount | null;
  },
  incoTerm?: string | null
): PricedAmount | null {
  return item.suggestedPricePerPiece != null &&
    item.suggestedPricePerPiece.amount > 0
    ? item.suggestedPricePerPiece
    : getListedPricePerPiece(item, incoTerm);
}

function getModeAwarePrice(
  item: {
    price?: PricedAmount | null;
    pickupPrice?: PricedAmount | null;
    deliveryPrice?: PricedAmount | null;
  },
  incoTerm?: string | null
): PricedAmount | null | undefined {
  const deliveryMethod = incoTermToDeliveryMethod(incoTerm);
  if (deliveryMethod === 'pickup') return item.pickupPrice;
  if (deliveryMethod === 'delivered') return item.deliveryPrice;
  return item.price;
}

export type TallyGroup<T> = {
  tallyId: string;
  isRandomTally: boolean;
  items: T[];
};

export function groupItemsByTally<
  T extends {
    tallyPlatformProduct?: { id: string } | null;
    cannedTally?: unknown;
  },
>(items: T[]): { tallyGroups: Map<string, TallyGroup<T>>; regularItems: T[] } {
  const tallyGroups = new Map<string, TallyGroup<T>>();
  const regularItems: T[] = [];
  for (const item of items) {
    const tallyId = item.tallyPlatformProduct?.id;
    if (tallyId) {
      const groupKey = `${tallyId}:${item.cannedTally ? 'canned' : 'random'}`;
      if (!tallyGroups.has(groupKey)) {
        tallyGroups.set(groupKey, {
          tallyId,
          isRandomTally: !item.cannedTally,
          items: [],
        });
      }
      tallyGroups.get(groupKey)!.items.push(item);
    } else {
      regularItems.push(item);
    }
  }
  return { tallyGroups, regularItems };
}

export function getItemFt(item: OrderItem): number {
  return Math.round(
    (item.platformProductsLengths?.length?.actualLengthInches ?? 0) / 12
  );
}

// Tally bundles exist only on exact whole-foot lengths; near-foot cut-to lengths
// (e.g. the 92.625" stud) must not claim a foot bucket.
export function wholeFootLength(actualLengthInches: number): number | null {
  return actualLengthInches > 0 && actualLengthInches % 12 === 0
    ? actualLengthInches / 12
    : null;
}

type TallyBundleLike = {
  platformProductsLength: {
    length?: { actualLengthInches?: number | null } | null;
  };
  bundleCount: number;
};

/**
 * Bundle counts keyed by exact whole foot. Bundles on any other length are dropped rather
 * than rounded, so every lookup into this map must key on whole feet too (getItemWholeFt).
 */
export function buildTallyBundlesByFt(
  bundles: readonly TallyBundleLike[] | null | undefined
): Record<number, number> {
  return Object.fromEntries(
    (bundles ?? []).flatMap((b) => {
      const ft = wholeFootLength(
        b.platformProductsLength.length?.actualLengthInches ?? 0
      );
      return ft === null ? [] : [[ft, b.bundleCount]];
    })
  );
}

export function getItemWholeFt(item: BundledLine): number | null {
  return wholeFootLength(
    item.platformProductsLengths?.length?.actualLengthInches ?? 0
  );
}

export function calcUnitsPerBundle(
  piecesPerBundle: number,
  unitCode: UnitCode,
  lengthInches: number,
  widthInches: number,
  thicknessInches: number
): number {
  return (
    piecesPerBundle *
    unitsPerPiece(unitCode, lengthInches, widthInches, thicknessInches)
  );
}

export type TallyBundleBreakdown = {
  actualLengthInches: number;
  bundleCount: number;
  piecesPerLf?: number | null;
  piecesPerBf?: number | null;
  piecesPerMbf?: number | null;
  piecesPerSf?: number | null;
  piecesPerMsf?: number | null;
  piecesPerCbm?: number | null;
};

/**
 * Maps a tally's raw bundle rows to {actualLengthInches, bundleCount, ...} pairs,
 * without rounding to feet. Rounding to feet is a display concern (and two
 * distinct lengths can round to the same foot value); calculations that feed
 * pricing or quantity must use the unrounded length.
 */
export function buildTallyBundleBreakdown(
  bundles: {
    bundleCount: number;
    platformProductsLength: {
      length?: { actualLengthInches?: number | null } | null;
      piecesPerLf?: number | null;
      piecesPerBf?: number | null;
      piecesPerMbf?: number | null;
      piecesPerSf?: number | null;
      piecesPerMsf?: number | null;
      piecesPerCbm?: number | null;
    };
  }[]
): TallyBundleBreakdown[] {
  return bundles.map((b) => ({
    actualLengthInches:
      b.platformProductsLength.length?.actualLengthInches ?? 0,
    bundleCount: b.bundleCount,
    piecesPerLf: b.platformProductsLength.piecesPerLf,
    piecesPerBf: b.platformProductsLength.piecesPerBf,
    piecesPerMbf: b.platformProductsLength.piecesPerMbf,
    piecesPerSf: b.platformProductsLength.piecesPerSf,
    piecesPerMsf: b.platformProductsLength.piecesPerMsf,
    piecesPerCbm: b.platformProductsLength.piecesPerCbm,
  }));
}

function piecesPerUnitFromBreakdown(
  bundle: TallyBundleBreakdown,
  unitCode: UnitCode
): number | null | undefined {
  switch (unitCode) {
    case 'LF':
      return bundle.piecesPerLf;
    case 'BF':
      return bundle.piecesPerBf;
    case 'MBF':
      return bundle.piecesPerMbf;
    case 'SF':
      return bundle.piecesPerSf;
    case 'MSF':
      return bundle.piecesPerMsf;
    case 'CBM':
      return bundle.piecesPerCbm;
    case 'PCS':
      return 1;
    case 'TALLIES':
      return undefined;
    default:
      return undefined;
  }
}

export function calcUnitsPerBundleForTallyLength(
  bundle: TallyBundleBreakdown,
  piecesPerBundle: number,
  unitCode: UnitCode
): number | undefined {
  const piecesPerUnit = piecesPerUnitFromBreakdown(bundle, unitCode);
  if (piecesPerUnit == null || piecesPerUnit === 0) return undefined;
  return piecesPerBundle / piecesPerUnit;
}

/**
 * Total quantity (in the given unit) that one tally represents, summed across
 * its bundle composition. Uses each length's own stored piecesPerUnit ratio
 * (platform_products_lengths, nominal- or actual-basis per that length's
 * is_nominal flag) — the same ratio the backend uses for pricing and stock —
 * rather than recomputing from the product's actual dimensions, which
 * disagrees with the backend whenever a length's ratio is nominal-basis.
 *
 * Returns undefined if the product has no piecesPerBundle configured, or if
 * any bundle is missing its piecesPerUnit ratio — a partial sum would
 * understate the true total and look like a valid price, which is worse than
 * surfacing "cannot compute" to the caller.
 */
export function calcUnitsPerTally(
  bundles: TallyBundleBreakdown[],
  product: {
    piecesPerBundle?: { value?: number | null } | null;
  },
  unitCode: UnitCode
): number | undefined {
  const piecesPerBundle = product.piecesPerBundle?.value;
  if (piecesPerBundle == null) return undefined;
  let total = 0;
  for (const bundle of bundles) {
    const piecesPerUnit = piecesPerUnitFromBreakdown(bundle, unitCode);
    if (piecesPerUnit == null || piecesPerUnit === 0) return undefined;
    total += (bundle.bundleCount * piecesPerBundle) / piecesPerUnit;
  }
  return total;
}

/**
 * Computes the exact whole-piece count for a bundle-quantity edit by first
 * recovering the (necessarily integer) bundle count — Math.round absorbs any
 * floating-point drift — then multiplying back out by piecesPerBundle.
 *
 * Exists because dividing a lossy, already-rounded displayed quantity
 * directly by unitsPerPiece can land a rounding-up consumer one whole piece
 * past what the buyer actually asked for (see order.test.ts for a worked
 * example). Recovering the bundle count first avoids that drift.
 *
 * Returns undefined when a whole bundle size or piecesPerBundle isn't
 * available (e.g. missing product dimensions, or piecesPerBundle is 0) —
 * callers should fall back to their own per-unit (unitsPerPiece-based)
 * conversion in that case. Without the piecesPerBundle guard, a 0 there
 * would multiply out to a "valid-looking" 0 piece count instead of
 * signaling unavailability, silently overwriting a real quantityStored
 * value with 0.
 */
export function computeExactPieces(
  quantityValue: number,
  piecesPerBundle: number,
  bundleSizeInUnit: number
): number | undefined {
  if (!(bundleSizeInUnit > 0) || !(piecesPerBundle > 0)) return undefined;
  const bundles = Math.round(quantityValue / bundleSizeInUnit);
  return bundles * piecesPerBundle;
}

/** Exact piece count for a quantity, rounding by `unitsPerPiece` when no bundle size is known. */
export function piecesForQuantity(
  quantityValue: number,
  piecesPerBundle: number,
  bundleSizeInUnit: number,
  unitsPerPiece: number | null | undefined
): number | undefined {
  return (
    computeExactPieces(quantityValue, piecesPerBundle, bundleSizeInUnit) ??
    (unitsPerPiece != null && unitsPerPiece > 0
      ? Math.round(quantityValue / unitsPerPiece)
      : undefined)
  );
}

export function getUnitsPerBundle(item: BundledLine): number | null {
  const product = item.platformProductsLengths?.platformProduct;
  if (!product) return null;
  const piecesPerBundle =
    item.effectivePiecesPerBundle > 0
      ? item.effectivePiecesPerBundle
      : (product.piecesPerBundle?.value ?? 0);
  if (piecesPerBundle <= 0) return null;
  const dims = getPlatformProductDimensions(
    product,
    item.platformProductsLengths?.length,
    item.platformProductsLengths?.isNominal
  );
  try {
    return calcUnitsPerBundle(
      piecesPerBundle,
      item.quantity.unit,
      dims.lengthInches,
      dims.widthInches,
      dims.thicknessInches
    );
  } catch {
    return null;
  }
}

export function getRandomTallyBundleCount(item: BundledLine): number | null {
  const unitsPerBundle = getUnitsPerBundle(item);
  if (!unitsPerBundle) return null;
  return item.quantity.value / unitsPerBundle;
}

export function getRandomTallyTotalPieces(item: OrderItem): number | null {
  const piecesPerBundle =
    item.platformProductsLengths?.platformProduct?.piecesPerBundle?.value;
  const bundleCount = getRandomTallyBundleCount(item);
  if (piecesPerBundle == null || bundleCount == null) return null;
  return bundleCount * piecesPerBundle;
}

export function computeTallyCount(
  items: BundledLine[],
  tallyBundlesByFt: Record<number, number | null>
): number {
  let totalQuantity = 0;
  let quantityPerTruck = 0;
  for (const item of items) {
    const ft = getItemWholeFt(item);
    const tallyBundles = ft === null ? null : tallyBundlesByFt[ft];
    const perBundle = getUnitsPerBundle(item);
    if (!perBundle) continue;
    totalQuantity += item.quantity.value;
    if (tallyBundles != null && tallyBundles > 0)
      quantityPerTruck += tallyBundles * perBundle;
  }
  if (quantityPerTruck === 0) return 1;
  const TALLY_EPSILON = 1e-9;
  return Math.max(
    1,
    Math.ceil(totalQuantity / quantityPerTruck - TALLY_EPSILON)
  );
}

export type CartItemUpdateInput = {
  platformProductInputFilter: { id: string; tally: boolean };
  locationId: string;
  quantity: { value: number; unit: UnitCode };
  sellerOrganizationFacilityId: string;
  parentOrderId?: string;
  piecesPerBundle?: number;
};

export function buildCartItemUpdateInput({
  productId,
  locationId,
  facilityId,
  quantity,
  unit,
  parentOrderId,
  piecesPerBundle,
  isTally = false,
}: {
  productId: string;
  locationId: string;
  facilityId: string;
  quantity: number;
  unit: UnitCode;
  parentOrderId?: string | null;
  piecesPerBundle?: number;
  isTally?: boolean;
}): CartItemUpdateInput {
  return {
    platformProductInputFilter: { id: productId, tally: isTally },
    locationId,
    quantity: { value: quantity, unit },
    sellerOrganizationFacilityId: facilityId,
    ...(parentOrderId ? { parentOrderId } : {}),
    ...(piecesPerBundle != null ? { piecesPerBundle } : {}),
  };
}

type TallyTaggable = { tallyPlatformProduct?: { id?: string | null } | null };
type CapacityLine = TallyTaggable & {
  platformProductsLengths?: {
    platformProductVehicleCapacities?: Array<VehicleCapacityPayload | null> | null;
  } | null;
};
type NamedCapacityLine = CapacityLine & {
  platformProductsLengths: { platformProduct: { name: string } };
};

// The server prices tally lines independently of vehicle capacity and does not check them.
export function isVehicleCapacityCheckedLine(item: TallyTaggable): boolean {
  return !item.tallyPlatformProduct?.id;
}

function hasCapacityFor(item: CapacityLine, vehicleId: string): boolean {
  return (
    getVehiclePayload(
      item.platformProductsLengths?.platformProductVehicleCapacities,
      vehicleId
    ) !== null
  );
}

export function linesWithoutCapacityFor<Line extends CapacityLine>(
  items: Line[],
  vehicleId: string
): Line[] {
  if (!vehicleId) return [];
  return items
    .filter(isVehicleCapacityCheckedLine)
    .filter((item) => !hasCapacityFor(item, vehicleId));
}

export function isPlaceOrderDisabled(
  internationalComplete: boolean,
  contactsLoaded: boolean,
  productsNeedingCapacity: string[]
): boolean {
  return (
    !internationalComplete ||
    !contactsLoaded ||
    productsNeedingCapacity.length > 0
  );
}

export function productNamesOf(lines: NamedCapacityLine[]): string[] {
  return [
    ...new Set(
      lines.map((line) => line.platformProductsLengths.platformProduct.name)
    ),
  ];
}

export type InheritedTransport = {
  incoTerm: LogisticsIncoterm;
  transportVehicleId: string;
};

type CartTransportSource = {
  id: string;
  isInternational?: boolean | null;
  incoTerm?: LogisticsIncoterm | null;
  transportVehicle?: { id: string } | null;
};

export function transportOfAnotherCart(
  carts: readonly CartTransportSource[],
  cart: Pick<CartTransportSource, 'id' | 'isInternational'>
): InheritedTransport | null {
  const source = carts.find(
    (other) =>
      other.id !== cart.id &&
      !!other.isInternational === !!cart.isInternational &&
      !!other.transportVehicle?.id &&
      !!other.incoTerm
  );
  if (!source?.transportVehicle || !source.incoTerm) return null;
  return {
    incoTerm: source.incoTerm,
    transportVehicleId: source.transportVehicle.id,
  };
}

export function initialDeliveryMethod({
  isInternational,
  savedIncoTerm,
  hasSavedVehicle,
  inheritedIncoTerm,
}: {
  isInternational: boolean;
  savedIncoTerm: LogisticsIncoterm | null | undefined;
  hasSavedVehicle: boolean;
  inheritedIncoTerm: LogisticsIncoterm | null;
}): DomesticDeliveryMethod | undefined {
  // International always behaves as delivered; the toggle is hidden in TransportCard.
  if (isInternational) return 'delivered';
  if (hasSavedVehicle || !inheritedIncoTerm) {
    return incoTermToDeliveryMethod(savedIncoTerm);
  }
  return incoTermToDeliveryMethod(inheritedIncoTerm);
}
